import { useState } from "react";
import { Share2, Copy, Check, Mail, MessageSquare, X } from "lucide-react";

/**
 * Share-this-page button — opens a small popover with copy-to-clipboard
 * and one-tap sharing via WhatsApp, Facebook, Text Message, and Email.
 *
 * Props:
 *   message  — short blurb appended to the URL when sharing (e.g.
 *              "Sign your queen up for Queenz Like Me!")
 *   testid   — base data-testid (default "share-page")
 */
export function SharePageButton({ message, testid = "share-page" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = message || "Check out Queenz Like Me — a girls mentoring program building tomorrow's queenz.";
  const shareText = `${text} ${url}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* silent */
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Queenz Like Me", text, url });
      } catch (_) {
        /* user cancelled */
      }
    } else {
      setOpen((o) => !o);
    }
  };

  const targets = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      bg: "#25D366",
      href: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    },
    {
      id: "sms",
      label: "Text Message",
      bg: "#34c759",
      Icon: MessageSquare,
      href: `sms:?&body=${encodeURIComponent(shareText)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      bg: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
    },
    {
      id: "email",
      label: "Email",
      bg: "#7d289b",
      Icon: Mail,
      href: `mailto:?subject=${encodeURIComponent("Queenz Like Me")}&body=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <div className="relative inline-block" data-testid={`${testid}-root`}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => {
          if (navigator.share) {
            nativeShare();
          } else {
            setOpen((o) => !o);
          }
        }}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#7d289b] text-white font-semibold text-sm hover:bg-[#4a1466] transition-colors shadow-lg"
      >
        <Share2 className="h-4 w-4" />
        Share this page
      </button>

      {open && (
        <>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close share menu"
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          />
          <div
            data-testid={`${testid}-menu`}
            className="absolute z-50 right-0 mt-2 w-72 rounded-2xl bg-white shadow-2xl border-2 border-[#d4af37] p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-bold text-[#4a1466]">Share this page</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-[#7d289b] hover:text-[#d62288]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <button
              type="button"
              data-testid={`${testid}-copy`}
              onClick={copyLink}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border-2 border-[#d4af37] bg-[#fff8e1] hover:bg-[#fff0c2] transition-colors text-sm text-[#1a0f14]"
            >
              <span className="truncate flex-1 text-left font-mono text-xs text-[#5c4a54]">
                {url}
              </span>
              <span className="flex items-center gap-1 font-semibold text-[#7d289b] shrink-0">
                {copied ? (
                  <>
                    <Check className="h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy
                  </>
                )}
              </span>
            </button>

            <div className="mt-3 grid grid-cols-4 gap-2">
              {targets.map((t) => (
                <a
                  key={t.id}
                  href={t.href}
                  target={t.id === "facebook" || t.id === "whatsapp" ? "_blank" : undefined}
                  rel={t.id === "facebook" || t.id === "whatsapp" ? "noreferrer" : undefined}
                  data-testid={`${testid}-${t.id}`}
                  onClick={() => setTimeout(() => setOpen(false), 200)}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl text-white hover:opacity-90 transition-opacity text-[10px] font-semibold"
                  style={{ background: t.bg }}
                  title={`Share via ${t.label}`}
                >
                  {t.Icon ? (
                    <t.Icon className="h-5 w-5" />
                  ) : t.id === "whatsapp" ? (
                    <WhatsAppGlyph />
                  ) : (
                    <FacebookGlyph />
                  )}
                  <span className="leading-none">{t.label}</span>
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function WhatsAppGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.4 0 .04 5.36.04 11.96c0 2.11.55 4.16 1.6 5.97L0 24l6.22-1.63a11.93 11.93 0 005.78 1.47h.01c6.6 0 11.96-5.36 11.96-11.96 0-3.19-1.24-6.19-3.45-8.4zM12 21.83h-.01a9.9 9.9 0 01-5.04-1.38l-.36-.21-3.69.97.99-3.6-.24-.37A9.86 9.86 0 012.14 12C2.14 6.55 6.55 2.14 12 2.14c2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 012.9 7c0 5.45-4.43 9.79-9.89 9.79zm5.43-7.39c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07a8.16 8.16 0 01-2.4-1.48 8.99 8.99 0 01-1.66-2.06c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.5 1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.08-.12-.27-.2-.57-.35z" />
    </svg>
  );
}

function FacebookGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M22 12.07C22 6.49 17.52 2 12 2S2 6.49 2 12.07C2 17.1 5.66 21.28 10.44 22v-7.02H7.9v-2.9h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.58v1.89h2.78l-.44 2.9h-2.34V22C18.34 21.28 22 17.1 22 12.07z" />
    </svg>
  );
}
